// externals
import * as React from 'react';

// libraries
import { INextStepCardDetails, IVehicleOption } from '@makemydeal/dr-activities-common';
import { IconFactory } from '@makemydeal/dr-activities-common';

// utils
import { formatDollarsAndCents } from '../../../../store/mmd/formatUtils';
import { milesToStringShort } from '../../../../store/mmd/uiHelpersUtil';
import { getBackRouteOfLoanBalance } from '../../../../utils/tradeUtils';

// config
import config from '../../../../store/mmd/config';
import * as tradeInRoutes from '../../../../utils/routes';

// components
import TradePage from '../../../../common/components/TradePage/TradePage';
import { errorMessageUserIsDealer, getErrorMessageUserIsNotDealer } from './constants';
import GTPFlowPageSubtitle from '../../GTPFlowPageSubtitle/GTPFlowPageSubtitle';
import useGTPRulesModal from '../../../../common/components/subaruEligibility/useGTPRulesModal';
import GTPRulesTrigger from '../../../../common/components/subaruEligibility/GTPRulesTrigger';
import GTPRulesModal from '../../../../common/components/subaruEligibility/GTPRulesModal';

// types
import { ITradeInSubaruGtpFlowError, StepperRoute } from '../../../../types';

export interface IManualSummaryUIStateProps {
    calculatedTradeInValue: number;
    cardDetails: INextStepCardDetails;
    currentDate: Date;
    dealerPhoneNumber: string;
    expirationDate: string;
    hasAcv: boolean;
    imageFPO: string;
    isSubaruGtpEligible?: boolean;
    isIcoTradeInFlow: boolean;
    isLeadFormEnabled: boolean;
    isLeadFormSubmitted: boolean;
    isManualEntryFlow: boolean;
    isTradeLeadFormEnabled: boolean;
    loanBalance: number;
    make: string;
    model: string;
    stepperRoutes?: StepperRoute[];
    subaruGtpLogoAlt?: any; // This is purely for local dev use (storybook can't show the static assets)
    tradeInValue: number;
    tradePaymentFailed: boolean;
    userIsDealer: boolean;
    userIsDesking: boolean;
    valueSource: string;
    vehicleCondition: string;
    vehicleId: string;
    vehicleMileage: number;
    vehicleOptions: IVehicleOption;
    vin?: string;
    year: string;
    zipCode: string;
    ghostVinDealerConfig: boolean;
    isGhostVinEnabled: boolean;
    actionButtonText: string;
    showActionButton: boolean;
    tradeInSubaruGtpFlowError: ITradeInSubaruGtpFlowError;
    tradeIndex?: number;
    isMultiTradeEnabled?: boolean;
    shouldIncludeTradeIn?: boolean;
    isAmdLienPayoffQuoteSpOnSPEnabled?: boolean;
    selectedLoanBalanceOption?: string;
    hasOpenLoan?: boolean | null;
    isSalesView?: boolean;
    isShowAppraisalValueScreenSalesView?: boolean;
    urlPath?: string;
}

export interface IManualSummaryUIDispatchProps {
    dispatchAnalytics: (type: string, description: string) => void;
    editTradeIn: () => void;
    goToRoute: (route: string) => void;
    loadLeadForm: (skiplink: boolean) => void;
    removeTrade: () => void;
    termsDetailsClicked: () => void;
    tradeInEstimateDisplayed: () => void;
    goToNextStep: () => void;
    cdlBackButtonClicked: () => void;
    addRemoveTradeToDeal: (shouldIncludeTradeIn: boolean) => void;
}

export interface IManualSummaryUIProps extends IManualSummaryUIStateProps, IManualSummaryUIDispatchProps {}

const ManualSummaryUI: React.FC<IManualSummaryUIProps> = (props) => {
    const {
        calculatedTradeInValue,
        dispatchAnalytics,
        expirationDate,
        goToRoute,
        isSubaruGtpEligible,
        hasAcv,
        make,
        model,
        stepperRoutes,
        tradeInValue,
        userIsDealer,
        userIsDesking,
        valueSource,
        vehicleCondition,
        vehicleMileage,
        year,
        loanBalance,
        ghostVinDealerConfig,
        isGhostVinEnabled,
        actionButtonText,
        showActionButton,
        goToNextStep,
        removeTrade,
        tradeInSubaruGtpFlowError,
        cdlBackButtonClicked,
        isMultiTradeEnabled,
        addRemoveTradeToDeal,
        shouldIncludeTradeIn,
        urlPath,
        isAmdLienPayoffQuoteSpOnSPEnabled,
        selectedLoanBalanceOption,
        hasOpenLoan,
        isSalesView,
        isShowAppraisalValueScreenSalesView,
        tradeIndex,
        isLeadFormEnabled,
        isLeadFormSubmitted,
        isTradeLeadFormEnabled,
        tradePaymentFailed,
        loadLeadForm,
        tradeInEstimateDisplayed,
        editTradeIn,
        dealerPhoneNumber
    } = props;

    const [showPaymentError, setShowPaymentError] = React.useState(tradePaymentFailed);
    const { showModal, openModal, closeModal } = useGTPRulesModal();

    React.useEffect(() => {
        if (!isLeadFormSubmitted && isLeadFormEnabled) {
            if (!userIsDealer) {
                loadLeadForm(!isTradeLeadFormEnabled);
            } else {
                tradeInEstimateDisplayed();
            }
        } else {
            tradeInEstimateDisplayed();
        }
    }, []);

    const didMountRef = React.useRef(false);
    React.useEffect(() => {
        if (didMountRef.current && isLeadFormSubmitted) {
            tradeInEstimateDisplayed();
        }
        didMountRef.current = true;
    }, [isLeadFormSubmitted]);

    React.useEffect(() => {
        setShowPaymentError(tradePaymentFailed);
    }, [tradePaymentFailed]);

    const isGtpExcluded = isSubaruGtpEligible === false && !!tradeInSubaruGtpFlowError?.errorType;

    const handlePencilEditClicked = () => {
        editTradeIn();
    };

    const renderError = () => {
        const errorMessage = userIsDealer ? errorMessageUserIsDealer : getErrorMessageUserIsNotDealer(dealerPhoneNumber);
        return showPaymentError ? (
            <div className="trade-in-payment-failed">
                <i className="fa fa-info-circle"></i>
                <p>
                    {errorMessage}
                    <a href="#" onClick={() => removeTrade()}>
                        Remove Trade-In
                    </a>
                </p>
            </div>
        ) : null;
    };

    const renderTradeConfirmationHeader = () => {
        const { staticImages } = config;

        return (
            <div className="trade-in-iw-estimate">
                {tradeInValue ? (
                    <IconFactory icon="checkmark" className="icon checkmark" staticImages={staticImages} />
                ) : (
                    <IconFactory icon="clock" className="icon clock" staticImages={staticImages} />
                )}
                <div className="estimated-value">
                    <div className="estimated-value__teaser common-header">
                        Your {year} {make} {model} has an
                    </div>
                    <div className={`estimated-value__content${tradeInValue === 0 || !tradeInValue ? '__pa' : ''} common-header`}>
                        <div className="estimated-value__content--label">Estimated Trade-in Value*:&nbsp;</div>
                        {tradeInValue && calculatedTradeInValue > 0 ? (
                            <div className="estimated-value__content--value positive">
                                {formatDollarsAndCents(calculatedTradeInValue)}
                            </div>
                        ) : null}
                        {tradeInValue && calculatedTradeInValue < 0 ? (
                            <div className="estimated-value__content--value negative">
                                {formatDollarsAndCents(calculatedTradeInValue)}
                            </div>
                        ) : null}
                        {tradeInValue === 0 || !tradeInValue ? (
                            <div className="estimated-value__content--value pending-appraisal">Pending Appraisal</div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    const pageSubTitle = () => (
        <GTPFlowPageSubtitle
            pageSubtitle="You shared your estimated trade-in value and we applied it to your deal."
            suffix={
                <>
                    Your deal has been updated with your vehicle's details.
                    {isGtpExcluded && (
                        <span className="trade-in-vehicle__gtprules">
                            &nbsp;See <GTPRulesTrigger onOpen={openModal} />
                        </span>
                    )}
                </>
            }
            tradeIndex={tradeIndex}
        />
    );

    const { staticImages /* renderMenuProducts */ } = config;
    const pageTitle = "Nice! We've Added Your Trade-In.";
    const pencilIcon = <IconFactory icon="pencilIcon" className="icon primary-link-icon" staticImages={staticImages} />;
    const editText = <span className="primary-link">Edit</span>;
    const shouldDisplayConditionLabel = vehicleCondition !== '';

    let dealerDealDescriptionText = '';
    let dealerDealLinkText = '';

    if (isGhostVinEnabled) {
        dealerDealDescriptionText = ghostVinDealerConfig ? 'Submit the completed deal to the CRM for review' : '';
        dealerDealLinkText = ghostVinDealerConfig ? 'Send to CRM' : 'Submit for Review';
    }

    const isAppraisalValueScreenHidden = isSalesView && !isShowAppraisalValueScreenSalesView;

    const backButtonRoute = isAmdLienPayoffQuoteSpOnSPEnabled
        ? getBackRouteOfLoanBalance(selectedLoanBalanceOption, hasOpenLoan)
        : isAppraisalValueScreenHidden
          ? tradeInRoutes.TRADE_IN_DECISION
          : tradeInRoutes.TRADE_MANUAL_ENTRY;

    const handleSkipButtonClick = () => {
        removeTrade();
    };

    const handleBackButtonClick = () => {
        cdlBackButtonClicked();
        goToRoute(backButtonRoute);
    };

    return (
        <>
            <TradePage
                urlPath={urlPath}
                stepperRoutes={stepperRoutes}
                isStepperClickable={false}
                stepLabelVisible={true}
                pageTitle={pageTitle}
                pageSubTitle={pageSubTitle()}
                headerOnTop
                showCustomTitle
                pageClass="trade-in-summary-page"
                showShareDeal={false}
                isDealerUser={userIsDealer}
                isDeskingUser={userIsDesking}
                staticAssetsConfig={staticImages}
                cardDetails={null}
                goToRoute={goToRoute}
                dispatchAnalytics={dispatchAnalytics}
                renderError={renderError}
                dealerDealDescription={dealerDealDescriptionText}
                dealerDealLinkText={dealerDealLinkText}
                isSubaruGtpEligible={isSubaruGtpEligible}
                tradeInSubaruGtpFlowError={tradeInSubaruGtpFlowError}
                {...(!isMultiTradeEnabled && {
                    footerProps: {
                        onActionButtonClick: showActionButton ? goToNextStep : undefined,
                        buttonText: actionButtonText,
                        showBackButton: true,
                        showSkipButton: true,
                        skipButtonText: 'Remove Trade-In',
                        fullWidthLayout: true,
                        onSkipButtonClick: handleSkipButtonClick,
                        onBackButtonClick: handleBackButtonClick
                    }
                })}
            >
                <div className="trade-in-summary-content">
                    {renderTradeConfirmationHeader()}
                    <div className="trade-in-iw-vehicle container-fluid">
                        <div className="vehicle">
                            <div className="row">
                                <div className="vehicle-info-details">
                                    <div className="estimate-header">How'd we come up with the value?</div>
                                    <div className="vehicle-info__content">
                                        <div className="vehicle-info__contentLeft">
                                            <div className="math-stack-iw-container">
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Est. Value*</div>
                                                    <div className="math-stack-iw-value">
                                                        {!!tradeInValue && formatDollarsAndCents(tradeInValue)}
                                                        {!tradeInValue && (
                                                            <div>
                                                                Pending <br className="hide-lg" />
                                                                Appraisal
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>{' '}
                                                {loanBalance > 0 && (
                                                    <div className="math-stack-iw">
                                                        <div className="math-stack-iw-label">Amount You Owe</div>
                                                        <div className="math-stack-iw-value">
                                                            {loanBalance ? '-' : ''}
                                                            {formatDollarsAndCents(loanBalance)}
                                                        </div>
                                                    </div>
                                                )}
                                                <hr className="bar" />
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Trade-In Value</div>
                                                    {tradeInValue && calculatedTradeInValue > 0 ? (
                                                        <div className="math-stack-iw-value positive">
                                                            {formatDollarsAndCents(calculatedTradeInValue)}
                                                        </div>
                                                    ) : null}
                                                    {tradeInValue && calculatedTradeInValue < 0 ? (
                                                        <div className="math-stack-iw-value negative">
                                                            {formatDollarsAndCents(calculatedTradeInValue)}
                                                        </div>
                                                    ) : null}
                                                    {!tradeInValue && (
                                                        <div className="math-stack-iw-value">
                                                            Pending <br className="hide-lg" />
                                                            Appraisal
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="vehicle-info common-header">
                                    <div className="estimate-header flex">
                                        <div>Trade-In Details</div>
                                        {!hasAcv && (
                                            <div
                                                className={'primary-link-container pencil-edit-icon'}
                                                onClick={handlePencilEditClicked}
                                            >
                                                {pencilIcon}
                                                {editText}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="vehicle-info__content">
                                    <div className="vehicle-info__contentLeft">
                                        <div className="math-stack-iw-container">
                                            <div className="math-stack-iw">
                                                <div className="math-stack-iw-label">Year</div>
                                                <div className="math-stack-iw-value">{year}</div>
                                            </div>
                                            <div className="math-stack-iw">
                                                <div className="math-stack-iw-label">Make</div>
                                                <div className="math-stack-iw-value">{make}</div>
                                            </div>
                                            <div className="math-stack-iw">
                                                <div className="math-stack-iw-label">Model</div>
                                                <div className="math-stack-iw-value">{model}</div>
                                            </div>
                                            {shouldDisplayConditionLabel && (
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Condition</div>
                                                    <div className="math-stack-iw-value">{vehicleCondition}</div>
                                                </div>
                                            )}
                                            <div className="math-stack-iw">
                                                <div className="math-stack-iw-label">Mileage</div>
                                                <div className="math-stack-iw-value">{milesToStringShort(vehicleMileage)}</div>
                                            </div>
                                            {valueSource && (
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">
                                                        Value <br className="hide-md" />
                                                        Provided by
                                                    </div>
                                                    <div className="math-stack-iw-value trade-in-source-value">{valueSource}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="trade-in-legal">
                            <p>
                                Your trade-in equity is the net of your trade-in value and any outstanding loan amount. Final value
                                of trade-in may vary depending on dealer appraisal. Estimate valid through&nbsp;
                                {new Date(expirationDate).toLocaleDateString()}.
                            </p>
                        </div>
                    </div>
                </div>
            </TradePage>
            {isMultiTradeEnabled && (
                <div className="trade-summary-footer">
                    <button className="back-button" onClick={handleBackButtonClick}>
                        <span className="back-icon">&lt;</span> Back
                    </button>
                    <div className="right-actions">
                        <div className="apply-trade-toggle">
                            <label className="toggle-label">
                                Apply Trade to Deal
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={shouldIncludeTradeIn}
                                        onChange={() => addRemoveTradeToDeal(!!shouldIncludeTradeIn)}
                                    />
                                    <span className="slider"></span>
                                </div>
                            </label>
                        </div>
                        <button className="delete-trade-button" onClick={handleSkipButtonClick}>
                            Delete Trade-In
                        </button>
                    </div>
                </div>
            )}
            {isGtpExcluded && <GTPRulesModal show={showModal} onClose={closeModal} dispatchAnalytics={dispatchAnalytics} />}
        </>
    );
};

export default ManualSummaryUI;
